import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';

import { MatIcon } from '@angular/material/icon';
import { MatRipple } from '@angular/material/core';
import { CdkScrollable } from '@angular/cdk/overlay';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';

import { fromEvent, Subject, takeUntil, throttleTime } from 'rxjs';

import { ERoutes } from 'src/app/core/enums/routes.enum';
import { EScrollPrefix } from 'src/app/core/enums/scroll-to.enum';
import {
  ETripCityInfoType,
  ETripPlannerView,
} from 'src/app/core/enums/trip-planner-view.enum';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { HeaderStateService } from 'src/app/core/services/header-state.service';
import { HotelsBookingProviderService } from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-provider.service';

import { SwitcherComponent } from 'src/app/shared/components/switcher/switcher.component';
import { PreviewMapComponent } from 'src/app/shared/maps/preview-map/preview-map.component';
import { TripPlannerInteractionComponent } from '../trip-planner-interaction/trip-planner-interaction.component';
import { TripPreferencesComponent } from '../trip-planner-interaction/components/trip-preferences/trip-preferences.component';
import { TripDestinationsListComponent } from '../trip-planner-interaction/components/trip-destinations-list/trip-destinations-list.component';
import { AiChatMobileComponent } from '../../../shared/components/mobile/ai-chat-mobile/ai-chat-mobile.component';
import { AiChatInputMobileComponent } from '../../../shared/components/mobile/ai-chat-input-mobile/ai-chat-input-mobile.component';
import { CarRentalPreviewComponent } from '../../car-rental/components/car-rental-preview/car-rental-preview.component';
import { BookFlightsLinkComponent } from '../../../shared/used-components/book-flights-link/book-flights-link.component';
import { TripPlanPrintComponent } from '../../../shared/components/trip-plan-print/trip-plan-print.component';
import { OccasionGeniusComponent } from '../../../shared/components/occasion-genius/occasion-genius.component';
import { TripCityInfoComponent } from '../trip-planner-interaction/components/trip-city-info/trip-city-info.component';
import { HOTEL_CLIENT } from '../../../shared/used-components/hotels-booking-container/hotels-booking-container.component';
import { ItineraryListMobileComponent } from '../../countries/components/mobile/itinerary-list-mobile/itinerary-list-mobile.component';
import { ShowAllMobileComponent } from '../../countries/components/mobile/show-all-mobile/show-all-mobile.component';

@Component({
    selector: 'app-mobile-trip-planner-interaction',
    templateUrl: './mobile-trip-planner-interaction.component.html',
    styleUrl: './mobile-trip-planner-interaction.component.scss',
    imports: [
        SwitcherComponent,
        PreviewMapComponent,
        MatSlideToggle,
        MatRipple,
        MatIcon,
        TripPreferencesComponent,
        TripDestinationsListComponent,
        MatMenu,
        MatMenuTrigger,
        MatMenuItem,
        NgOptimizedImage,
        AiChatMobileComponent,
        AiChatInputMobileComponent,
        CarRentalPreviewComponent,
        BookFlightsLinkComponent,
        TripCityInfoComponent,
        OccasionGeniusComponent,
        ItineraryListMobileComponent,
        TripPlanPrintComponent,
        CdkScrollable,
        AsyncPipe,
        NgxSkeletonLoaderModule,
    ],
    providers: [
        {
            provide: HOTEL_CLIENT,
            useClass: HotelsBookingProviderService,
        },
    ]
})
export class MobileTripPlannerInteractionComponent
  extends TripPlannerInteractionComponent
  implements OnInit, AfterViewInit
{
  public readonly eRoutes = ERoutes;

  selectedTabView: ERoutes = ERoutes.TRIP_PLANNER;
  selectedTabViewIndex: null | string = null;
  hotelFilters: MatBottomSheetRef<ShowAllMobileComponent> | null = null;

  bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  headerStateService: HeaderStateService = inject(HeaderStateService);

  private contentScrollListener$: Subject<void> = new Subject<void>();

  @ViewChild('overviewEl', { static: false })
  private overviewEl!: ElementRef;

  ngOnInit() {
    super.ngOnInit();

    this.headerStateService.backBtnClickEmmit
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.hotelFilters) {
          this.headerStateService.isBackBtnPresent = false;
          this.hotelFilters.dismiss();
          this.hotelFilters = null;
        }
      });
  }

  ngAfterViewInit() {
    this.checkContentScrollListener(1500);
  }

  override selectService(index: number): void {
    if (index < 6) {
      this.plannerView = ETripPlannerView.CITY_OVERVIEW;
      if (this.cityInfoIndex === -1) {
        this.cityInfoIndex = 0;
      }
      if (index === 1) {
        this.showFirstCityHotels();
      }
    } else if (index === 6) {
      this.plannerView = ETripPlannerView.FLIGHTS_BOX;
      this.scrollToTop();
    } else if (index === 7) {
      this.plannerView = ETripPlannerView.CAR_RENTAL_BOX;
      this.scrollToTop();
    }
  }

  onServicesViewHandler(ev: ETripPlannerView): void {
    this.scrollToTop();
    this.changePlannerView(ev);
    this.checkContentScrollListener();
  }

  showFirstCityHotels(): void {
    this.headerStateService.isBackBtnPresent = true;

    this.hotelFilters = this.bottomSheet.open(ShowAllMobileComponent, {
      hasBackdrop: false,
      closeOnNavigation: true,
      panelClass: ['custom-bottom-sheet', 'custom-bottom-sheet--filters'],
      data: {
        items: [],
        pageType: ETripCityInfoType.BOOKING,
        currentDestinationIndex: 0,
        destinations: this.createdTrip,
        packages$: this.packageService.packages$,
        cityId: this.createdTrip?.destinations[0]?.id,
        cityName: this.createdTrip?.destinations[0]?.cityName,
        countryName: this.createdTrip?.destinations[0]?.countryName,
      },
    });
  }

  onSeeAllPopularTripsHandler(): void {
    this.selectedTabView = this.eRoutes.TRIP_POPULAR_ITINERARIES;
    this.selectedTabViewIndex = '2';
    this.scrollToTop();
    this.cdr.markForCheck();
    setTimeout(() => (this.selectedTabViewIndex = null), 1500);
  }

  onMakeItMyTripPlanHandler(index: number): void {
    this.selectedTabView = this.eRoutes.TRIP_PLANNER;
    this.selectedTabViewIndex = String(index);
    this.scrollToTop();
    this.cdr.markForCheck();
    setTimeout(() => (this.selectedTabViewIndex = null), 1500);
  }

  onScrollToElementHandler(elementIndex: string): void {
    this.changePlannerView(ETripPlannerView.CITY_OVERVIEW);
    this.cdr.markForCheck();
    setTimeout(() => {
      this.scrollToIdService.scrollToWithContainer(
        +elementIndex,
        EScrollPrefix.ELEMENT,
        '.mobile-planner__services-info',
        150,
      );
    }, 300);
  }

  private checkContentScrollListener(timeOut: number = 500): void {
    if (this.contentScrollListener$) {
      this.contentScrollListener$.next();
      this.contentScrollListener$.complete();
    }

    setTimeout(() => {
      if (this.plannerView === ETripPlannerView.PLANNER_OVERVIEW) {
        if (this.overviewEl && this.overviewEl.nativeElement) {
          fromEvent(this.overviewEl.nativeElement, 'scroll')
            .pipe(takeUntil(this.contentScrollListener$), throttleTime(25))
            .subscribe(() => this.scrollToBottom());
        }
      }
    }, timeOut);
  }
}
